import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import { useT } from "../lib/i18n";

const COPY = {
    en: {
        eyebrow: "Privacy",
        title: "Privacy Policy",
        commitment_title: "Our commitment",
        commitment:
            "ScottsClub is committed to protecting user privacy. Users' phone numbers, photographs, private contact information, and personal data will never be displayed, shared, sold, or made accessible to other users without explicit consent. Personal information is protected using industry-standard security measures and is used solely for providing and improving the service.",
        what_title: "What we collect",
        what_items: [
            "First and last name — used to greet you at the door.",
            "A self-uploaded photo — used by door staff to recognise you.",
            "Phone number — used only as your unique member identifier and, where applicable, to send membership-related notifications (e.g. an approval SMS).",
            "Operational data — visit timestamps and check-in events, used to manage capacity at the venue.",
        ],
        how_title: "How we use it",
        how_items: [
            "Reviewing your membership application.",
            "Recognising you at the door and granting entry as a member.",
            "Counting live occupancy and venue capacity in real time.",
            "Service improvement and security — strictly internal.",
        ],
        access_title: "Who can see what",
        access_items: [
            "Other members CANNOT see your phone number, photo, name, or any of your personal information.",
            "Only authorised ScottsClub administrators and named door staff (on shift, with a personal PIN) can view member details necessary for entry.",
            "Photographs are served exclusively through authenticated, signed URLs — never on a public link.",
        ],
        rights_title: "Your rights",
        rights_items: [
            "You may request a copy of your data at any time.",
            "You may request correction or deletion of your data at any time.",
            "If approved, you may edit your name and photograph yourself from your member page.",
        ],
        retention_title: "Data retention",
        retention:
            "We retain your application and visit history for as long as you remain a member. If your application is rejected or your membership is closed, we delete identifying data on request.",
        contact_title: "Contact",
        contact:
            "Questions or requests regarding your data: write to the team at John Scotts Kungsträdgården, Hamngatan 19, Stockholm.",
        back: "Back",
    },
    sv: {
        eyebrow: "Integritet",
        title: "Integritetspolicy",
        commitment_title: "Vårt åtagande",
        commitment:
            "ScottsClub värnar om medlemmarnas integritet. Telefonnummer, foton, kontaktinformation och personuppgifter visas, delas eller säljs aldrig till andra användare utan uttryckligt samtycke. Personuppgifter skyddas med säkerhetsstandarder i branschen och används endast för att tillhandahålla och förbättra tjänsten.",
        what_title: "Vad vi samlar in",
        what_items: [
            "För- och efternamn — för att hälsa dig vid dörren.",
            "En självuppladdad bild — för att dörrpersonalen ska känna igen dig.",
            "Mobilnummer — används endast som din unika medlemsidentitet och för att skicka relevanta medlemsmeddelanden (t.ex. ett godkännande-SMS).",
            "Driftdata — tidsstämplar för besök och incheckningar, används för kapacitetshantering.",
        ],
        how_title: "Hur vi använder informationen",
        how_items: [
            "Granska din medlemsansökan.",
            "Känna igen dig vid dörren och släppa in dig som medlem.",
            "Räkna live-beläggning och kapacitet i lokalen i realtid.",
            "Säkerhet och förbättring av tjänsten — strikt internt.",
        ],
        access_title: "Vem ser vad",
        access_items: [
            "Andra medlemmar kan INTE se ditt telefonnummer, din bild, ditt namn eller annan personlig information.",
            "Endast auktoriserade ScottsClub-administratörer och namngiven dörrpersonal (på pass, med personlig PIN) kan se de medlemsuppgifter som krävs vid insläpp.",
            "Bilder serveras enbart via autentiserade, signerade URL:er — aldrig via en publik länk.",
        ],
        rights_title: "Dina rättigheter",
        rights_items: [
            "Du kan när som helst begära en kopia av din data.",
            "Du kan när som helst begära rättelse eller radering av din data.",
            "Om du är godkänd kan du själv redigera namn och bild från din medlemssida.",
        ],
        retention_title: "Lagring",
        retention:
            "Vi sparar din ansökan och besökshistorik så länge du är medlem. Om din ansökan avslås eller ditt medlemskap avslutas raderar vi identifierande data på begäran.",
        contact_title: "Kontakt",
        contact:
            "Frågor eller önskemål om dina uppgifter: skriv till teamet på John Scotts Kungsträdgården, Hamngatan 19, Stockholm.",
        back: "Tillbaka",
    },
};

const Section = ({ title, children }) => (
    <section className="mt-10">
        <h2 className="font-serif text-2xl text-ink-primary">{title}</h2>
        <div className="gold-divider opacity-30 my-4 w-24" />
        <div className="text-ink-secondary leading-relaxed text-[15px]">{children}</div>
    </section>
);

export default function Privacy() {
    const { lang, t } = useT();
    const c = COPY[lang] || COPY.en;

    return (
        <div className="min-h-[100dvh] text-ink-primary relative" data-testid="privacy-page">
            <Header />
            <main className="relative max-w-2xl mx-auto px-6 pt-32 pb-24">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="label-eyebrow flex items-center gap-2">
                        <ShieldCheck size={14} className="text-gold" />
                        {c.eyebrow}
                    </div>
                    <h1 className="font-serif font-medium text-4xl md:text-5xl mt-4 leading-tight">{c.title}</h1>
                </motion.div>

                <Section title={c.commitment_title}>
                    <p className="text-balance">{c.commitment}</p>
                </Section>

                <Section title={c.what_title}>
                    <ul className="space-y-2 list-disc pl-5">
                        {c.what_items.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                </Section>

                <Section title={c.how_title}>
                    <ul className="space-y-2 list-disc pl-5">
                        {c.how_items.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                </Section>

                <Section title={c.access_title}>
                    <ul className="space-y-2 list-disc pl-5">
                        {c.access_items.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                </Section>

                <Section title={c.rights_title}>
                    <ul className="space-y-2 list-disc pl-5">
                        {c.rights_items.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                </Section>

                <Section title={c.retention_title}>
                    <p>{c.retention}</p>
                </Section>

                <Section title={c.contact_title}>
                    <p>{c.contact}</p>
                </Section>

                <div className="mt-12">
                    <Link to="/" className="inline-flex items-center gap-2 text-gold text-sm tracking-[0.22em] uppercase hover:text-gold-bright">
                        <ArrowLeft size={16} /> {c.back}
                    </Link>
                </div>
            </main>
        </div>
    );
}
